import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { LeadsLayout } from '@/components/LeadsLayout';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import * as XLSX from 'xlsx';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#3B82F6', '#8B5CF6', '#F59E0B', '#10B981', '#EC4899', '#F97316'];

// Date format helper - DD-MM-YYYY
const formatDateDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Get first day of current month in YYYY-MM-DD format (timezone safe)
const getFirstDayOfMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
};

// Get today in YYYY-MM-DD format (timezone safe)
const getToday = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function LeadRepPerformance({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState([]);
  const [dateRange, setDateRange] = React.useState({
    from: getFirstDayOfMonth(),
    to: getToday()
  });

  React.useEffect(() => {
    fetchData();
  }, [dateRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.from) params.append('date_from', dateRange.from);
      if (dateRange.to) params.append('date_to', dateRange.to);
      
      const response = await axios.get(`${BACKEND_URL}/api/leads/analytics/rep-performance?${params.toString()}`);
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load report data');
    }
    setLoading(false);
  };

  // Calculate totals
  const totals = React.useMemo(() => {
    return data.reduce((acc, item) => ({
      assigned: acc.assigned + item.assigned,
      contacted: acc.contacted + item.contacted,
      test_drive: acc.test_drive + item.test_drive,
      won: acc.won + item.won,
      lost: acc.lost + item.lost
    }), { assigned: 0, contacted: 0, test_drive: 0, won: 0, lost: 0 });
  }, [data]);

  // Bar chart data
  const barData = data.filter(d => d.rep !== 'Unassigned').map(item => ({
    name: item.rep.length > 12 ? item.rep.substring(0, 12) + '...' : item.rep,
    'Assigned': item.assigned,
    'Won': item.won,
    'Lost': item.lost
  }));

  // Get top performer
  const topPerformer = data.filter(d => d.rep !== 'Unassigned').sort((a, b) => b.won - a.won)[0];

  // Export to Excel
  const exportToExcel = () => {
    const exportData = data.map(item => ({
      'Sales Rep': item.rep,
      'Assigned': item.assigned,
      'Contacted': item.contacted,
      'Test Drive': item.test_drive,
      'Won': item.won,
      'Lost': item.lost,
      'Conversion Rate %': item.conversion_rate
    }));
    
    exportData.push({
      'Sales Rep': 'TOTAL',
      'Assigned': totals.assigned,
      'Contacted': totals.contacted,
      'Test Drive': totals.test_drive,
      'Won': totals.won,
      'Lost': totals.lost,
      'Conversion Rate %': totals.assigned > 0 ? ((totals.won / totals.assigned) * 100).toFixed(1) : '0'
    });
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales Rep Performance');
    XLSX.writeFile(wb, `Sales_Rep_Performance_${dateRange.from}_to_${dateRange.to}.xlsx`);
    toast.success('Report exported to Excel');
  };

  return (
    <LeadsLayout user={user} onLogout={onLogout} title="Sales Rep Lead Performance">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800" data-testid="report-title">Sales Rep Lead Performance</h2>
            <p className="text-sm text-gray-500">Track lead conversion performance by sales representative • <span className="font-medium text-gray-700">{data.length} reps, {totals.assigned} total leads</span></p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">From:</label>
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                data-testid="date-from"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">To:</label>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                data-testid="date-to"
              />
            </div>
            <div className="relative group">
              <button className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm">
                📥 Export
              </button>
              <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border hidden group-hover:block z-10">
                <button onClick={exportToExcel} className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100">📊 Export to Excel</button>
                <button onClick={() => window.print()} className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100">📄 Print/PDF</button>
              </div>
            </div>
            <button
              onClick={() => navigate('/leads')}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              ← Back
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        ) : (
          <>
            {/* Top Performer & Summary */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* Top Performer Card */}
              {topPerformer && (
                <div className="md:col-span-2 rounded-2xl p-6 text-white shadow-[0_8px_28px_rgba(5,150,105,0.30)]" style={{ background: 'linear-gradient(135deg, #059669 0%, #0D9488 60%, #0891B2 100%)' }}>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center text-2xl font-bold">
                      🏆
                    </div>
                    <div>
                      <p className="text-emerald-100 text-sm">Top Performer</p>
                      <p className="text-2xl font-bold">{topPerformer.rep}</p>
                      <p className="text-emerald-100">{topPerformer.won} conversions ({topPerformer.conversion_rate}%)</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Summary Cards */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <p className="text-2xl font-bold text-blue-700">{totals.assigned}</p>
                <p className="text-sm text-blue-600">Total Assigned</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                <p className="text-2xl font-bold text-green-700">{totals.won}</p>
                <p className="text-sm text-green-600">Total Won</p>
              </div>
              <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                <p className="text-2xl font-bold text-red-700">{totals.lost}</p>
                <p className="text-sm text-red-600">Total Lost</p>
              </div>
            </div>

            {/* Bar Chart */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Performance Comparison</h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Assigned" fill="#3B82F6" />
                  <Bar dataKey="Won" fill="#22C55E" />
                  <Bar dataKey="Lost" fill="#EF4444" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Data Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-bold text-gray-800">Detailed Performance</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="rep-table">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sales Rep</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Assigned</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Contacted</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Test Drive</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Won</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Lost</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Conv. Rate</th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ranking</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.map((item, idx) => (
                      <tr key={item.rep} className={`hover:bg-gray-50 ${item.rep === topPerformer?.rep ? 'bg-green-50' : ''}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {item.rep === topPerformer?.rep && <span>🏆</span>}
                            <span className="font-medium text-gray-800">{item.rep}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center font-medium">{item.assigned}</td>
                        <td className="px-6 py-4 text-center">{item.contacted}</td>
                        <td className="px-6 py-4 text-center">{item.test_drive}</td>
                        <td className="px-6 py-4 text-center">
                          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">{item.won}</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-sm">{item.lost}</span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                            item.conversion_rate >= 30 ? 'bg-green-100 text-green-700' :
                            item.conversion_rate >= 15 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {item.conversion_rate}%
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          {item.rep !== 'Unassigned' && (
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                              idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                              idx === 1 ? 'bg-gray-200 text-gray-700' :
                              idx === 2 ? 'bg-orange-100 text-orange-700' :
                              'bg-gray-100 text-gray-500'
                            }`}>
                              #{idx + 1}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {/* Totals Row */}
                    <tr className="bg-gray-100 font-bold">
                      <td className="px-6 py-4">TOTAL</td>
                      <td className="px-6 py-4 text-center">{totals.assigned}</td>
                      <td className="px-6 py-4 text-center">{totals.contacted}</td>
                      <td className="px-6 py-4 text-center">{totals.test_drive}</td>
                      <td className="px-6 py-4 text-center">{totals.won}</td>
                      <td className="px-6 py-4 text-center">{totals.lost}</td>
                      <td className="px-6 py-4 text-center">
                        {totals.assigned > 0 ? ((totals.won / totals.assigned) * 100).toFixed(1) : 0}%
                      </td>
                      <td className="px-6 py-4 text-center">-</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </LeadsLayout>
  );
}

export default LeadRepPerformance;
